import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest, apiTokenRequest } from '../config/authConfig';
import apiClient from '../config/axiosConfig';
import {
  DEV_MODE,
  getDevIdentityEmail,
  setDevIdentityEmail,
  type DevIdentity,
  type DevIdentitiesResponse,
} from '../config/devIdentity';

export type Role = 'superadmin' | 'beheerder' | 'none';

export interface ManagedLocation {
  id: number;
  name: string | null;
  city: string | null;
  receive_emails: boolean;
}

export interface User {
  name: string;
  email: string;
  role: Role;
  isSuperAdmin: boolean;
  /** True zodra iemand een rol heeft; wie niets mag ziet het "geen toegang"-scherm. */
  isAdmin: boolean;
  /** True bij de eerste keer inloggen: er moet nog een locatie gekozen worden. */
  needsLocationChoice: boolean;
  locations: ManagedLocation[];
}

interface ProfileResponse {
  success: boolean;
  data: {
    email: string;
    name: string | null;
    role: Role;
    is_superadmin: boolean;
    needs_location_choice: boolean;
    locations: ManagedLocation[];
  };
}

export interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  login: () => Promise<void>;
  logout: () => void;
  refreshProfile: () => Promise<User | null>;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  needsLocationChoice: boolean;
  role: Role;
  locations: ManagedLocation[];
  isMsalReady: boolean;
  /** Alleen lokaal gevuld: testaccounts waarmee je van rol kunt wisselen. */
  devIdentities: DevIdentity[];
  devIdentityEmail: string | null;
  switchDevIdentity: (email: string) => void;
}

/**
 * Microsoft tells us who someone is; the backend (/api/me) tells us what they
 * may do. The role is never decided in the browser, only displayed.
 */
const fetchProfile = async (fallbackName: string, fallbackEmail: string): Promise<User> => {
  const response = await apiClient.get<ProfileResponse>('/me');
  const profile = response.data.data;

  return {
    name: profile.name || fallbackName,
    email: profile.email || fallbackEmail,
    role: profile.role,
    isSuperAdmin: profile.is_superadmin,
    isAdmin: profile.role !== 'none',
    needsLocationChoice: Boolean(profile.needs_location_choice),
    locations: profile.locations || [],
  };
};

const noAccessUser = (name: string, email: string): User => ({
  name,
  email,
  role: 'none',
  isSuperAdmin: false,
  isAdmin: false,
  needsLocationChoice: false,
  locations: [],
});

/**
 * Alle login-logica staat bewust in één provider. Zou elk scherm zijn eigen
 * kopie draaien, dan zou de Microsoft-redirect meerdere keren afgehandeld
 * worden en zou /api/me onnodig vaak bevraagd worden.
 */
const useAuthState = (): AuthContextValue => {
  const { instance, accounts, inProgress } = useMsal();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isMsalReady, setIsMsalReady] = useState(false);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [devIdentities, setDevIdentities] = useState<DevIdentity[]>([]);
  const [devIdentityEmail, setDevIdentityEmailState] = useState<string | null>(getDevIdentityEmail());
  // Zolang we nog niet weten wie er is ingelogd, blijven we "laden" tonen.
  // Anders flitst het scherm "je hebt geen toegang" even voorbij.
  const [isInitialised, setIsInitialised] = useState(false);

  const loadProfile = useCallback(async (fallbackName: string, fallbackEmail: string) => {
    setIsProfileLoading(true);
    try {
      const profile = await fetchProfile(fallbackName, fallbackEmail);
      setUser(profile);
      localStorage.setItem('user', JSON.stringify(profile));
      return profile;
    } catch (error) {
      console.error('Kon de rechten van deze gebruiker niet ophalen:', error);
      const fallback = noAccessUser(fallbackName, fallbackEmail);
      setUser(fallback);
      return fallback;
    } finally {
      setIsProfileLoading(false);
    }
  }, []);

  // DEV MODE: skip the Microsoft login, but still ask the backend for the role
  // so local testing matches production behaviour.
  useEffect(() => {
    if (!DEV_MODE) return;

    setIsMsalReady(true);
    if (localStorage.getItem('dev_logged_out')) {
      setIsInitialised(true);
      return;
    }

    loadProfile('Dev Admin', getDevIdentityEmail() || 'dev@admin.com').finally(() =>
      setIsInitialised(true)
    );
  }, [loadProfile]);

  // Alleen lokaal: welke testaccounts mag deze omgeving aannemen? Staat de
  // rolwissel uit, dan bestaat dit adres niet en blijft de lijst leeg.
  useEffect(() => {
    if (!DEV_MODE) return;

    let cancelled = false;
    apiClient
      .get<DevIdentitiesResponse>('/dev/identities')
      .then((response) => {
        if (cancelled) return;
        const { identities, current } = response.data.data;
        setDevIdentities(identities || []);
        setDevIdentityEmailState(current);
        // Stond er nog een adres opgeslagen dat niet meer mag? Dan is de server
        // teruggevallen op de standaard en ruimen we de oude keuze op.
        if (getDevIdentityEmail() && getDevIdentityEmail() !== current) {
          setDevIdentityEmail(current);
        }
      })
      .catch(() => {
        if (!cancelled) setDevIdentities([]);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Wisselt van testidentiteit en laadt de pagina opnieuw. Herladen is bewust:
   * zo verdwijnt alle informatie van de vorige rol uit het scherm en begin je
   * schoon, precies zoals iemand die net inlogt.
   */
  const switchDevIdentity = useCallback((email: string) => {
    if (!DEV_MODE) return;
    setDevIdentityEmail(email);
    localStorage.removeItem('user');
    localStorage.removeItem('dev_logged_out');
    window.location.reload();
  }, []);

  // Check if MSAL instance is properly initialized
  useEffect(() => {
    if (instance && typeof instance.getActiveAccount === 'function') {
      setIsMsalReady(true);
    }
  }, [instance]);

  const login = useCallback(async () => {
    if (DEV_MODE) {
      localStorage.removeItem('dev_logged_out');
      await loadProfile('Dev Admin', 'dev@admin.com');
      return;
    }

    try {
      // Check if MSAL instance is properly initialized
      if (!instance || !instance.getActiveAccount || !isMsalReady) {
        console.error('MSAL instance not properly initialized or not ready');
        return;
      }

      setIsLoading(true);
      // Use redirect instead of popup for more reliable authentication
      await instance.loginRedirect(loginRequest);
    } catch (error: unknown) {
      console.error('Login error:', error);

      if (error && typeof error === 'object' && 'errorCode' in error) {
        const msalError = error as { errorCode: string };
        if (msalError.errorCode === 'consent_required') {
          console.error('User consent required. Please contact your administrator.');
        } else if (msalError.errorCode === 'interaction_required') {
          console.error('User interaction required. Please try logging in again.');
        }
      }

      setIsLoading(false);
    }
  }, [instance, isMsalReady, loadProfile]);

  const logout = useCallback(() => {
    if (DEV_MODE) {
      setUser(null);
      localStorage.setItem('dev_logged_out', 'true');
      localStorage.removeItem('user');
      localStorage.removeItem('accessToken');
      window.location.href = '/';
      return;
    }

    // Clear local storage first
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('accessToken');

    // Clear MSAL cache
    if (instance) {
      instance.clearCache();
    }

    // Instead of using Azure logout (which can get stuck),
    // just redirect to home page directly
    window.location.href = '/';
  }, [instance]);

  // Handle redirect response when user returns from authentication
  useEffect(() => {
    if (DEV_MODE) return;

    const handleRedirectResponse = async () => {
      try {
        const response = await instance.handleRedirectPromise();
        const account = response?.account;
        if (!account) return;

        const tokenResponse = await instance.acquireTokenSilent({
          ...apiTokenRequest,
          account,
        });
        localStorage.setItem('accessToken', tokenResponse.accessToken);

        await loadProfile(account.name || 'Onbekende gebruiker', account.username || '');
      } catch (error) {
        console.error('Error handling redirect response:', error);
      }
    };

    if (isMsalReady) {
      handleRedirectResponse();
    }
  }, [instance, isMsalReady, loadProfile]);

  const checkAuthStatus = useCallback(async () => {
    if (DEV_MODE) return;

    // Wait for MSAL to be ready
    if (inProgress !== 'none' || !isMsalReady) {
      return;
    }

    if (accounts.length > 0) {
      const account = accounts[0];
      try {
        const tokenResponse = await instance.acquireTokenSilent({
          ...apiTokenRequest,
          account,
        });
        localStorage.setItem('accessToken', tokenResponse.accessToken);

        await loadProfile(account.name || 'Onbekende gebruiker', account.username || '');
      } catch (error) {
        console.error('Token acquisition error:', error);
        logout();
      }
    } else {
      // Check if user info exists in localStorage
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
    }

    setIsInitialised(true);
  }, [accounts, instance, loadProfile, logout, inProgress, isMsalReady]);

  useEffect(() => {
    if (DEV_MODE) return;

    // Only check auth status when MSAL is ready
    if (inProgress === 'none' && isMsalReady) {
      checkAuthStatus();
    }
  }, [checkAuthStatus, inProgress, isMsalReady]);

  const refreshProfile = useCallback(async () => {
    if (!user) return null;
    return loadProfile(user.name, user.email);
  }, [loadProfile, user]);

  return {
    user,
    isLoading: DEV_MODE
      ? !isInitialised || isProfileLoading
      : !isInitialised || isLoading || inProgress !== 'none' || !isMsalReady || isProfileLoading,
    login,
    logout,
    refreshProfile,
    isAuthenticated: !!user,
    isAdmin: user?.isAdmin || false,
    isSuperAdmin: user?.isSuperAdmin || false,
    needsLocationChoice: user?.needsLocationChoice || false,
    role: user?.role || 'none',
    locations: user?.locations || [],
    isMsalReady: DEV_MODE ? true : isMsalReady,
    devIdentities,
    devIdentityEmail,
    switchDevIdentity,
  };
};

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const value = useAuthState();
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth moet binnen een <AuthProvider> gebruikt worden.');
  }
  return context;
};
